import React, { useState, useEffect } from 'react';
import './DDRLayoutManager.css';
import { apiFetch } from '../api/api';
import HTMLEditor from '../components/HTMLEditor';
import CSSEditor from '../components/CSSEditor';
// Import shared format options
import {
  TEXT_FORMAT_OPTIONS,
  DATE_FORMAT_OPTIONS,
  TIME_FORMAT_OPTIONS,
  NUMBER_FORMAT_OPTIONS,
  CURRENCY_FORMAT_OPTIONS,
  COORDINATE_FORMAT_OPTIONS,
  FIELD_WIDTH_OPTIONS,
  FIELD_FORMAT_TYPES,
  FIELD_INPUT_OPTIONS,
  COLUMN_TYPES,
  getDefaultFormatSettings,
  mapColumnTypeToFormatType,
  normalizeFormatValue,
  generateFieldConfig,
  isFieldLookup
} from '../utils/formatters';

const DDRLayoutManager = () => {
  const [columnConfig, setColumnConfig] = useState([]);
  const [layoutConfig, setLayoutConfig] = useState({
    headerHTML: `<div class="report-main-header">
      <h1 class="report-main-title">Daily Drilling Report (DDR)</h1>
      <p class="report-subtitle">{{wellName}} | {{reportDate}}</p>
    </div>`,
    globalStyles: {
      fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      fontSize: '14px',
      textColor: '#1e293b',
      backgroundColor: '#ffffff',
      sectionSpacing: '20px',
      sectionPadding: '16px',
      reportContainerPadding: '20px',
      reportContainerSpacing: '0px',
      borderColor: '#e5e7eb',
      borderRadius: '8px',
      sectionHeaderFontSize: '16px',
      sectionHeaderBackgroundColor: '#f8fafc',
      sectionHeaderTextColor: '#1e293b',
      sectionHeaderFontWeight: '600',
      customCSS: ''
    },
    sections: [
      {
        id: 'wellData',
        name: 'Well Data',
        enabled: true,
        presentationType: 'label-value',
        order: 0,
        tableName: 'Well',
        fields: [
          { id: 'wellName', name: 'Well Name', enabled: true, format: 'text', width: 'auto', dbColumn: 'WellName', group: 1 },
          { id: 'operator', name: 'Operator', enabled: true, format: 'text', width: 'auto', dbColumn: 'Operator', group: 1 },
          { id: 'field', name: 'Field Name', enabled: true, format: 'text', width: 'auto', dbColumn: 'Field', group: 1 },
          { id: 'wellType', name: 'Well Type', enabled: true, format: 'text', width: 'auto', dbColumn: 'WellType', group: 2 },
          { id: 'kbElevation', name: 'KB Elevation (m)', enabled: true, format: 'number', width: 'auto', dbColumn: 'OriginalKB', group: 2 },
          { id: 'groundElevation', name: 'Ground Elevation (m)', enabled: true, format: 'number', width: 'auto', dbColumn: 'GroundElevation', group: 2 },
          { id: 'latitude', name: 'Latitude', enabled: true, format: 'coordinate', coordinateFormat: 'dms-hemisphere', width: 'auto', dbColumn: 'Latitude', group: 3 },
          { id: 'longitude', name: 'Longitude', enabled: true, format: 'coordinate', coordinateFormat: 'dms-hemisphere', width: 'auto', dbColumn: 'Longitude', group: 3 }
        ],
        groupSettings: {
          1: { itemsPerRow: 3, customCSS: '', name: 'Basic Info', showGroupName: true },
          2: { itemsPerRow: 2, customCSS: '', name: 'Technical Details', showGroupName: true },
          3: { itemsPerRow: 2, customCSS: '', name: 'Location', showGroupName: true }
        }
      },
      {
        id: 'wellPlan',
        name: 'Well Plan',
        enabled: true,
        presentationType: 'label-value',
        order: 1,
        tableName: 'WellPlan',
        fields: [
          { id: 'plannedTD', name: 'Planned TD (mKB)', enabled: true, format: 'number', width: 'auto', dbColumn: 'PlannedTD', group: 1 },
          { id: 'targetFormation', name: 'Target Formation', enabled: true, format: 'text', width: 'auto', dbColumn: 'TargetFormation', group: 1 }
        ],
        groupSettings: {
          1: { itemsPerRow: 2, customCSS: '', name: 'Planning Data', showGroupName: false }
        }
      },
      {
        id: 'wellDailyData',
        name: 'Well Daily Data',
        enabled: true,
        presentationType: 'label-value',
        order: 2,
        tableName: 'DailyWellData',
        fields: [
          { id: 'depthStart', name: 'Depth Start (mKB)', enabled: true, format: 'number', width: 'auto', dbColumn: 'DepthStart', group: 1 },
          { id: 'depthEnd', name: 'Depth End (mKB)', enabled: true, format: 'number', width: 'auto', dbColumn: 'DepthEnd', group: 1 },
          { id: 'drillingHours', name: 'Drilling Hours (hrs)', enabled: true, format: 'number', width: 'auto', dbColumn: 'DrillingHours', group: 1 },
          { id: 'dailyCost', name: 'Daily Cost ($)', enabled: true, format: 'currency', width: 'auto', dbColumn: 'DailyCost', group: 2 },
          { id: 'cumulativeCost', name: 'Cumulative Cost ($)', enabled: true, format: 'currency', width: 'auto', dbColumn: 'CumulativeCost', group: 2 },
          { id: 'nptHours', name: 'NPT Hours', enabled: true, format: 'number', width: 'auto', dbColumn: 'NPT_Hours', group: 2 },
          { id: 'comments', name: 'Comments', enabled: true, format: 'textarea', width: 'full', dbColumn: 'Comments', group: 3 }
        ],
        groupSettings: {
          1: { itemsPerRow: 3, customCSS: '', name: 'Depth & Hours', showGroupName: true },
          2: { itemsPerRow: 3, customCSS: '', name: 'Cost Data', showGroupName: true },
          3: { itemsPerRow: 1, customCSS: '', name: 'Comments', showGroupName: false }
        }
      },
      {
        id: 'operationsSummary',
        name: 'Operations Summary',
        enabled: true,
        presentationType: 'table',
        order: 3,
        tableName: 'DailyActivity',
        fields: [
          { id: 'startTime', name: 'Start', enabled: true, format: 'time', width: '100px', dbColumn: 'StartTime', group: 1 },
          { id: 'endTime', name: 'End', enabled: true, format: 'time', width: '100px', dbColumn: 'EndTime', group: 1 },
          { id: 'duration', name: 'Duration (hrs)', enabled: true, format: 'number', width: '120px', dbColumn: 'DurationHours', group: 1 },
          { id: 'category', name: 'Category', enabled: true, format: 'text', width: '120px', dbColumn: 'Category', group: 1 },
          { id: 'code1', name: 'Code 1', enabled: true, format: 'text', width: '100px', dbColumn: 'Code1', group: 1 },
          { id: 'code2', name: 'Code 2', enabled: true, format: 'text', width: '100px', dbColumn: 'Code2', group: 1 },
          { id: 'code3', name: 'Code 3', enabled: true, format: 'text', width: '100px', dbColumn: 'Code3', group: 1 },
          { id: 'description', name: 'Comment', enabled: true, format: 'textarea', width: 'auto', dbColumn: 'Description', group: 1 }
        ],
        groupSettings: {
          1: { itemsPerRow: 1, customCSS: '', name: 'Activities', showGroupName: false }
        }
      },
      {
        id: 'supervisorContact',
        name: 'Supervisor Contact',
        enabled: true,
        presentationType: 'table',
        order: 4,
        tableName: '', // No table yet
        fields: [
          { id: 'name', name: 'Name', enabled: true, format: 'text', width: 'auto', dbColumn: '', group: 1 },
          { id: 'position', name: 'Position', enabled: true, format: 'text', width: 'auto', dbColumn: '', group: 1 }
        ],
        groupSettings: {
          1: { itemsPerRow: 1, customCSS: '', name: 'Contact Info', showGroupName: false }
        }
      },
      {
        id: 'hsseData',
        name: 'HSSE Data',
        enabled: true,
        presentationType: 'table',
        order: 5,
        tableName: '', // No table yet
        fields: [
          { id: 'type', name: 'Type', enabled: true, format: 'text', width: '150px', dbColumn: '', group: 1 },
          { id: 'lastDate', name: 'Last Date', enabled: true, format: 'date', width: '120px', dbColumn: '', group: 1 },
          { id: 'nextDate', name: 'Next Date', enabled: true, format: 'date', width: '120px', dbColumn: '', group: 1 }
        ],
        groupSettings: {
          1: { itemsPerRow: 1, customCSS: '', name: 'Safety Data', showGroupName: false }
        }
      },
      {
        id: 'pob',
        name: 'POB (Personnel On Board)',
        enabled: true,
        presentationType: 'table',
        order: 6,
        tableName: 'POB',
        fields: [
          { id: 'personnel', name: 'Personnel', enabled: true, format: 'text', width: 'auto', dbColumn: 'Name', group: 1 },
          { id: 'count', name: 'Count', enabled: true, format: 'number', width: '100px', dbColumn: 'Position', group: 1 }
        ],
        groupSettings: {
          1: { itemsPerRow: 1, customCSS: '', name: 'Personnel', showGroupName: false }
        }
      },
      {
        id: 'weather',
        name: 'Weather',
        enabled: true,
        presentationType: 'label-value',
        order: 7,
        tableName: '', // No table yet
        fields: [
          { id: 'temperature', name: 'Temperature (°C)', enabled: true, format: 'number', width: 'auto', dbColumn: '', group: 1 },
          { id: 'windSpeed', name: 'Wind Speed (km/h)', enabled: true, format: 'number', width: 'auto', dbColumn: '', group: 1 },
          { id: 'seaState', name: 'Sea State', enabled: true, format: 'text', width: 'auto', dbColumn: '', group: 2 },
          { id: 'visibility', name: 'Visibility', enabled: true, format: 'text', width: 'auto', dbColumn: '', group: 2 }
        ],
        groupSettings: {
          1: { itemsPerRow: 2, customCSS: '', name: 'Weather Conditions', showGroupName: true },
          2: { itemsPerRow: 2, customCSS: '', name: 'Visibility', showGroupName: true }
        }
      },
      {
        id: 'bha',
        name: 'BHA (Bottom Hole Assembly)',
        enabled: true,
        presentationType: 'table',
        order: 8,
        tableName: '', // No table yet
        fields: [
          { id: 'item', name: 'Item', enabled: true, format: 'text', width: '100px', dbColumn: '', group: 1 },
          { id: 'description', name: 'Description', enabled: true, format: 'text', width: 'auto', dbColumn: '', group: 1 },
          { id: 'length', name: 'Length (m)', enabled: true, format: 'number', width: '120px', dbColumn: '', group: 1 },
          { id: 'od', name: 'OD (mm)', enabled: true, format: 'number', width: '100px', dbColumn: '', group: 1 },
          { id: 'id', name: 'ID (mm)', enabled: true, format: 'number', width: '100px', dbColumn: '', group: 1 }
        ],
        groupSettings: {
          1: { itemsPerRow: 1, customCSS: '', name: 'BHA Components', showGroupName: false }
        }
      },
      {
        id: 'casing',
        name: 'Casing',
        enabled: true,
        presentationType: 'table',
        order: 9,
        tableName: '', // No table yet
        fields: [
          { id: 'string', name: 'String', enabled: true, format: 'text', width: '100px', dbColumn: '', group: 1 },
          { id: 'size', name: 'Size (in)', enabled: true, format: 'number', width: '100px', dbColumn: '', group: 1 },
          { id: 'grade', name: 'Grade', enabled: true, format: 'text', width: '100px', dbColumn: '', group: 1 },
          { id: 'weight', name: 'Weight (ppf)', enabled: true, format: 'number', width: '120px', dbColumn: '', group: 1 },
          { id: 'depth', name: 'Depth (m)', enabled: true, format: 'number', width: '120px', dbColumn: '', group: 1 }
        ],
        groupSettings: {
          1: { itemsPerRow: 1, customCSS: '', name: 'Casing Data', showGroupName: false }
        }
      },
      {
        id: 'mudData',
        name: 'Mud Data',
        enabled: true,
        presentationType: 'label-value',
        order: 10,
        tableName: '', // No table yet
        fields: [
          { id: 'mudWeight', name: 'Mud Weight (ppg)', enabled: true, format: 'number', width: 'auto', dbColumn: '', group: 1 },
          { id: 'viscosity', name: 'Viscosity (sec)', enabled: true, format: 'number', width: 'auto', dbColumn: '', group: 1 },
          { id: 'pv', name: 'PV (cP)', enabled: true, format: 'number', width: 'auto', dbColumn: '', group: 2 },
          { id: 'yp', name: 'YP (lbf/100ft²)', enabled: true, format: 'number', width: 'auto', dbColumn: '', group: 2 },
          { id: 'gelStrength', name: 'Gel Strength (lbf/100ft²)', enabled: true, format: 'number', width: 'auto', dbColumn: '', group: 3 },
          { id: 'ph', name: 'pH', enabled: true, format: 'number', width: 'auto', dbColumn: '', group: 3 }
        ],
        groupSettings: {
          1: { itemsPerRow: 2, customCSS: '', name: 'Basic Properties', showGroupName: true },
          2: { itemsPerRow: 2, customCSS: '', name: 'Rheology', showGroupName: true },
          3: { itemsPerRow: 2, customCSS: '', name: 'Chemical Properties', showGroupName: true }
        }
      },
      {
        id: 'deviationData',
        name: 'Deviation Data',
        enabled: true,
        presentationType: 'table',
        order: 11,
        tableName: '', // No table yet - could be linked to survey data in future
        fields: [
          { id: 'depth', name: 'Depth (m)', enabled: true, format: 'number', width: '120px', dbColumn: '', group: 1 },
          { id: 'inclination', name: 'Inclination (°)', enabled: true, format: 'number', width: '120px', dbColumn: '', group: 1 },
          { id: 'azimuth', name: 'Azimuth (°)', enabled: true, format: 'number', width: '120px', dbColumn: '', group: 1 },
          { id: 'tvd', name: 'TVD (m)', enabled: true, format: 'number', width: '120px', dbColumn: '', group: 1 },
          { id: 'ns', name: 'NS (m)', enabled: true, format: 'number', width: '120px', dbColumn: '', group: 1 },
          { id: 'ew', name: 'EW (m)', enabled: true, format: 'number', width: '120px', dbColumn: '', group: 1 }
        ],
        groupSettings: {
          1: { itemsPerRow: 1, customCSS: '', name: 'Survey Data', showGroupName: false }
        }
      }
    ]
  });

  const [selectedSection, setSelectedSection] = useState(null);
  const [toast, setToast] = useState('');
  const [availableTables, setAvailableTables] = useState([]);
  const [tableColumns, setTableColumns] = useState({});
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [showHeaderEditor, setShowHeaderEditor] = useState(false);
  const [showGlobalStylesEditor, setShowGlobalStylesEditor] = useState(false);
  const [newSectionData, setNewSectionData] = useState({
    name: '',
    presentationType: 'label-value',
    tableName: ''
  });
  const [showGroupSettings, setShowGroupSettings] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]); // Add state for multiple column selection
  
  // Enhanced state for improved multi-column functionality
  const [columnFilter, setColumnFilter] = useState('');
  const [selectedColumnTypes, setSelectedColumnTypes] = useState([]);
  const [bulkGroupAssignment, setBulkGroupAssignment] = useState(1);
  const [previewMode, setPreviewMode] = useState(false);

  // CSS Templates for styling
  const cssTemplates = {
    default: {
      name: 'Default',
      css: `
.field-group {
  background: #ffffff;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-bottom: 16px;
}
.field-group-title {
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
  font-size: 1rem;
}
.field-group .label {
  color: #6b7280;
  font-weight: 500;
}
.field-group .value {
  color: #111827;
  font-weight: normal;
}`
    },
    highlighted: {
      name: 'Highlighted',
      css: `
.field-group {
  background: linear-gradient(135deg, #f3f4f6 0%, #ffffff 100%);
  padding: 20px;
  border-radius: 12px;
  border: 2px solid #3b82f6;
  margin-bottom: 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}
.field-group-title {
  font-weight: 700;
  color: #1e40af;
  margin-bottom: 16px;
  font-size: 1.1rem;
  text-transform: uppercase;
}
.field-group .label {
  color: #374151;
  font-weight: 600;
}
.field-group .value {
  color: #1f2937;
  font-weight: 500;
}`
    },
    compact: {
      name: 'Compact',
      css: `
.field-group {
  background: #f9fafb;
  padding: 8px 12px;
  border-radius: 4px;
  border: none;
  margin-bottom: 8px;
}
.field-group-title {
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  font-size: 0.875rem;
}
.field-group .label {
  color: #6b7280;
  font-weight: 400;
  font-size: 0.875rem;
}
.field-group .value {
  color: #111827;
  font-weight: normal;
  font-size: 0.875rem;
}`
    },
    colorful: {
      name: 'Colorful',
      css: `
.field-group {
  background: linear-gradient(135deg, #fef3c7 0%, #fcd34d 50%, #f59e0b 100%);
  padding: 16px;
  border-radius: 12px;
  border: none;
  margin-bottom: 16px;
  color: #92400e;
}
.field-group-title {
  font-weight: 700;
  color: #92400e;
  margin-bottom: 12px;
  font-size: 1rem;
  text-shadow: 0 1px 2px rgba(146, 64, 14, 0.1);
}
.field-group .label {
  color: #78350f;
  font-weight: 600;
}
.field-group .value {
  color: #451a03;
  font-weight: 600;
}`
    },
    excelTable: {
      name: 'Excel Table',
      css: `
.field-group {
  background: #ffffff;
  border: 1px solid #d0d7de;
  margin-bottom: 0;
  padding: 0;
  border-radius: 0;
}
.field-group-title {
  font-weight: 600;
  color: #24292f;
  margin: 0;
  padding: 8px 12px;
  font-size: 0.875rem;
  background: #f6f8fa;
  border-bottom: 1px solid #d0d7de;
}
.field-group .field-grid {
  border-collapse: collapse;
}
.field-group .field-row {
  border: 1px solid #d0d7de;
  display: flex;
  flex-direction: column;
}
.field-group .field-row .label {
  color: #24292f;
  font-weight: 500;
  font-size: 0.875rem;
  padding: 4px 8px;
  border-bottom: 1px solid #d0d7de;
  background: #f6f8fa;
  display: flex;
  align-items: center;
}
.field-group .field-row .value {
  color: #24292f;
  font-weight: normal;
  font-size: 0.875rem;
  padding: 4px 8px;
  background: #ffffff;
  display: flex;
  align-items: center;
  min-height: 24px;
}`
    },
    excelCompact: {
      name: 'Excel Compact',
      css: `
.field-group {
  background: #ffffff;
  border: 1px solid #c6c6c6;
  margin-bottom: 0;
  padding: 0;
  border-radius: 0;
  box-shadow: none;
}
.field-group-title {
  font-weight: 600;
  color: #000000;
  margin: 0;
  padding: 4px 8px;
  font-size: 0.8125rem;
  background: #e7e6e6;
  border-bottom: 1px solid #c6c6c6;
}
.field-group .field-grid {
  border-collapse: collapse;
}
.field-group .field-row {
  border: 1px solid #c6c6c6;
  display: flex;
  flex-direction: column;
}
.field-group .field-row .label {
  color: #000000;
  font-weight: 400;
  font-size: 0.8125rem;
  padding: 2px 6px;
  border-bottom: 1px solid #c6c6c6;
  background: #f2f2f2;
  line-height: 1.2;
  display: flex;
  align-items: center;
}
.field-group .field-row .value {
  color: #000000;
  font-weight: normal;
  font-size: 0.8125rem;
  padding: 2px 6px;
  background: #ffffff;
  line-height: 1.2;
  display: flex;
  align-items: center;
  min-height: 20px;
}`
    },
    excelBordered: {
      name: 'Excel Bordered',
      css: `
.field-group {
  background: #ffffff;
  border: 2px solid #a6a6a6;
  margin-bottom: 2px;
  padding: 0;
  border-radius: 0;
  box-shadow: none;
}
.field-group-title {
  font-weight: 700;
  color: #000000;
  margin: 0;
  padding: 6px 10px;
  font-size: 0.875rem;
  background: #d9d9d9;
  border-bottom: 2px solid #a6a6a6;
  text-align: center;
}
.field-group .field-grid {
  border-collapse: collapse;
}
.field-group .field-row {
  border: 1px solid #a6a6a6;
  display: flex;
  flex-direction: column;
}
.field-group .field-row .label {
  color: #000000;
  font-weight: 500;
  font-size: 0.875rem;
  padding: 4px 8px;
  border-bottom: 1px solid #a6a6a6;
  background: #f0f0f0;
  display: flex;
  align-items: center;
}
.field-group .field-row .value {
  color: #000000;
  font-weight: normal;
  font-size: 0.875rem;
  padding: 4px 8px;
  background: #ffffff;
  display: flex;
  align-items: center;
  min-height: 24px;
}`
    },
    excelGrid: {
      name: 'Excel Grid',
      css: `
.field-group {
  background: #ffffff;
  border: 1px solid #808080;
  margin-bottom: 0;
  padding: 0;
  border-radius: 0;
  box-shadow: none;
}
.field-group-title {
  font-weight: 600;
  color: #000000;
  margin: 0;
  padding: 5px 8px;
  font-size: 0.8125rem;
  background: #cccccc;
  border-bottom: 1px solid #808080;
}
.field-group .field-grid {
  border-collapse: collapse;
}
.field-group .field-row {
  border: 1px solid #808080;
  display: flex;
  flex-direction: column;
}
.field-group .field-row .label {
  color: #000000;
  font-weight: 400;
  font-size: 0.8125rem;
  padding: 1px 4px;
  border-bottom: 1px solid #808080;
  background: #eeeeee;
  line-height: 1.1;
  display: flex;
  align-items: center;
}
.field-group .field-row .value {
  color: #000000;
  font-weight: normal;
  font-size: 0.8125rem;
  padding: 1px 4px;
  background: #ffffff;
  line-height: 1.1;
  display: flex;
  align-items: center;
  min-height: 18px;
}`
    }
  };

  // CSS Templates for table styling
  const tableCssTemplates = {
    default: {
      name: 'Default Table',
      css: `
table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
th {
  background: #f8fafc;
  color: #374151;
  font-weight: 600;
  padding: 12px 16px;
  text-align: left;
  border-bottom: 2px solid #e5e7eb;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
td {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #111827;
}
tr:hover {
  background: #f8fafc;
}
tr:last-child td {
  border-bottom: none;
}`
    },
    striped: {
      name: 'Striped Table',
      css: `
table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
th {
  background: #3b82f6;
  color: #ffffff;
  font-weight: 600;
  padding: 16px;
  text-align: left;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
td {
  padding: 14px 16px;
  color: #374151;
}
tr:nth-child(even) {
  background: #f8fafc;
}
tr:nth-child(odd) {
  background: #ffffff;
}
tr:hover {
  background: #e0f2fe !important;
}`
    },
    modern: {
      name: 'Modern Table',
      css: `
table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin: 16px 0;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}
th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  font-weight: 700;
  padding: 16px 20px;
  text-align: left;
  font-size: 0.875rem;
  text-transform: capitalize;
  border: none;
}
td {
  padding: 16px 20px;
  color: #374151;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.9rem;
}
tr:hover {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  transform: scale(1.01);
  transition: all 0.2s ease;
}
tr:last-child td {
  border-bottom: none;
}`
    },
    dark: {
      name: 'Dark Table',
      css: `
table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  background: #1f2937;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}
th {
  background: #111827;
  color: #f9fafb;
  font-weight: 600;
  padding: 16px;
  text-align: left;
  border-bottom: 2px solid #374151;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
td {
  padding: 12px 16px;
  border-bottom: 1px solid #374151;
  color: #d1d5db;
}
tr:hover {
  background: #374151;
}
tr:last-child td {
  border-bottom: none;
}`
    },
    minimal: {
      name: 'Minimal Table',
      css: `
table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  background: transparent;
}
th {
  background: transparent;
  color: #374151;
  font-weight: 600;
  padding: 8px 12px;
  text-align: left;
  border-bottom: 2px solid #d1d5db;
  font-size: 0.875rem;
}
td {
  padding: 8px 12px;
  color: #6b7280;
  border: none;
}
tr {
  border-bottom: 1px solid #f1f5f9;
}
tr:hover {
  background: #f9fafb;
}
tr:last-child {
  border-bottom: none;
}`
    },
    excelSheet: {
      name: 'Excel Sheet',
      css: `
table {
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  background: #ffffff;
  border: 1px solid #d0d7de;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
th {
  background: #f6f8fa;
  color: #24292f;
  font-weight: 600;
  padding: 6px 8px;
  text-align: left;
  border: 1px solid #d0d7de;
  font-size: 0.875rem;
  line-height: 1.2;
}
td {
  padding: 6px 8px;
  color: #24292f;
  border: 1px solid #d0d7de;
  font-size: 0.875rem;
  line-height: 1.2;
  vertical-align: middle;
}
tr:hover {
  background: #f1f5f9;
}`
    },
    excelCompactTable: {
      name: 'Excel Compact',
      css: `
table {
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  background: #ffffff;
  border: 1px solid #c6c6c6;
  font-family: 'Segoe UI', Arial, sans-serif;
}
th {
  background: #e7e6e6;
  color: #000000;
  font-weight: 600;
  padding: 3px 6px;
  text-align: left;
  border: 1px solid #c6c6c6;
  font-size: 0.8125rem;
  line-height: 1.1;
}
td {
  padding: 3px 6px;
  color: #000000;
  border: 1px solid #c6c6c6;
  font-size: 0.8125rem;
  line-height: 1.1;
  vertical-align: middle;
}
tr:hover {
  background: #f2f2f2;
}`
    },
    excelBorderedTable: {
      name: 'Excel Bordered',
      css: `
table {
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  background: #ffffff;
  border: 2px solid #a6a6a6;
  font-family: 'Segoe UI', Arial, sans-serif;
}
th {
  background: #d9d9d9;
  color: #000000;
  font-weight: 700;
  padding: 5px 8px;
  text-align: center;
  border: 1px solid #a6a6a6;
  font-size: 0.875rem;
  line-height: 1.2;
}
td {
  padding: 5px 8px;
  color: #000000;
  border: 1px solid #a6a6a6;
  font-size: 0.875rem;
  line-height: 1.2;
  vertical-align: middle;
}
tr:hover {
  background: #f0f0f0;
}`
    },
    excelGridTable: {
      name: 'Excel Grid',
      css: `
table {
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  background: #ffffff;
  border: 1px solid #808080;
  font-family: 'Segoe UI', Arial, sans-serif;
}
th {
  background: #cccccc;
  color: #000000;
  font-weight: 600;
  padding: 2px 4px;
  text-align: left;
  border: 1px solid #808080;
  font-size: 0.8125rem;
  line-height: 1.1;
}
td {
  padding: 2px 4px;
  color: #000000;
  border: 1px solid #808080;
  font-size: 0.8125rem;
  line-height: 1.1;
  vertical-align: middle;
}
tr:hover {
  background: #eeeeee;
}`
    }
  };

  // Load configuration from backend on component mount
  useEffect(() => {
    const loadConfiguration = async () => {
      try {
        const response = await apiFetch('/api/ddr-layout/config');
        // Migrate existing configuration to support groups
        const migratedConfig = migrateConfigurationToGroups(response);
        setLayoutConfig(migratedConfig);
      } catch (err) {
        console.error('Failed to load config from backend:', err);
        showToast('⚠️ Using default configuration - could not load from server');
      }
    };

    const loadColumnConfig = async () => {
      try {
        const response = await apiFetch('/api/config/columns');
        setColumnConfig(response);
        // Extract available tables from config_column
        const tables = response.map(table => ({
          name: table.tableName,
          label: table.tableName
        }));
        setAvailableTables(tables);
      } catch (err) {
        console.error('Failed to load column config:', err);
        // Fallback to current hardcoded tables
        const commonTables = [
          'Well', 'WellPlan', 'DailyWellData', 'DailyActivity', 'POB', 
          'Weather', 'WellLog', 'WellPerforation'
        ];
        setAvailableTables(commonTables.map(table => ({ name: table, label: table })));
      }
    };
    
    loadConfiguration();
    loadColumnConfig();
  }, []);

  // Helper function to get lookup configuration from column config
  const getLookupConfigFromColumnConfig = (tableName, columnName) => {
    if (!columnConfig || !Array.isArray(columnConfig)) return null;
    
    const tableConfig = columnConfig.find(config => config.tableName === tableName);
    if (!tableConfig || !tableConfig.columns) return null;
    
    const columnInfo = tableConfig.columns.find(col => col.name === columnName);
    if (!columnInfo) return null;
    
    // Check if column has lookup configuration
    if (columnInfo.options === 'lookup' && 
        columnInfo.lookupTable && 
        columnInfo.lookupValueColumn && 
        columnInfo.lookupDisplayColumn) {
      return {
        lookupTable: columnInfo.lookupTable,
        lookupValueColumn: columnInfo.lookupValueColumn,
        lookupDisplayColumn: columnInfo.lookupDisplayColumn,
        lookupReturnColumn: columnInfo.lookupReturnColumn || null
      };
    }
    
    return null;
  };

  // Migration function to add group support to existing configurations
  const migrateConfigurationToGroups = (config) => {
    if (!config || !config.sections) {
      return config;
    }

    // Add default header HTML if not present
    const headerHTML = config.headerHTML || `<div class="report-main-header">
      <h1 class="report-main-title">Daily Drilling Report (DDR)</h1>
      <p class="report-subtitle">{{wellName}} | {{reportDate}}</p>
    </div>`;

    return {
      ...config,
      headerHTML,
      sections: config.sections.map(section => {
        // Add groupSettings if not present
        if (!section.groupSettings) {
          section.groupSettings = {
            1: { itemsPerRow: 2, customCSS: '', name: 'Default Group', showGroupName: false }
          };
        } else {
          // Add showGroupName to existing groups if missing
          Object.keys(section.groupSettings).forEach(groupId => {
            if (section.groupSettings[groupId] && !section.groupSettings[groupId].hasOwnProperty('showGroupName')) {
              section.groupSettings[groupId].showGroupName = false;
            }
          });
        }
        // Add customTableCSS if not present
        if (!('customTableCSS' in section)) {
          section.customTableCSS = '';
        }
        // Add group property to fields if not present
        if (section.fields) {
          section.fields = section.fields.map(field => ({
            ...field,
            group: field.group || 1
          }));
        }
        return section;
      })
    };
  };

  // Format options for different data types - use imported FIELD_FORMAT_TYPES
  const formatOptions = FIELD_FORMAT_TYPES;

  // Format options are now imported from ../utils/formatters
  // This ensures consistency across all configuration managers



  // Currency formatting options


  // Common database tables that could be used
  const commonTables = [
    'Well', 'DailyWellData', 'DailyActivity', 'POB', 'WellPlan', 
    'JobCode', 'User', 'Attachment'
  ];

  // Load available tables on component mount
  useEffect(() => {
    setAvailableTables(commonTables);
  }, []);

  // Fetch table columns from config_column or API
  const fetchTableColumns = async (tableName) => {
    if (!tableName || tableColumns[tableName]) return;
    
    console.log('Fetching columns for table:', tableName);
    setLoadingColumns(true);
    try {
      // First try to get columns from config_column
      const tableConfig = columnConfig.find(table => table.tableName === tableName);
      if (tableConfig && tableConfig.columns) {
        console.log('Using config_column data for', tableName);
        // Convert config_column format to expected format
        const columns = tableConfig.columns.map(col => ({
          COLUMN_NAME: col.name,
          DATA_TYPE: col.type || 'string',
          IS_NULLABLE: 'YES',
          COLUMN_DEFAULT: null,
          COLUMN_COMMENT: col.desc || '',
          // Add config_column specific data
          label: col.label,
          unit: col.unit,
          format: col.type
        }));
        
        setTableColumns(prev => ({
          ...prev,
          [tableName]: columns
        }));
      } else {
        // Fallback to API call if not found in config_column
        console.log('Falling back to API for', tableName);
        const response = await apiFetch(`/api/db/columnlist/${tableName}`);
        console.log('Column response for', tableName, ':', response);
        setTableColumns(prev => ({
          ...prev,
          [tableName]: response
        }));
      }
    } catch (error) {
      console.error('Failed to fetch table columns:', error);
      showToast('Failed to fetch table columns: ' + error.message);
    } finally {
      setLoadingColumns(false);
    }
  };

  // Update section table mapping
  const updateSectionTable = (sectionId, tableName) => {
    setLayoutConfig(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? { ...section, tableName }
          : section
      )
    }));

    // Fetch columns for the new table
    if (tableName) {
      fetchTableColumns(tableName);
    }
  };

  const updateSectionKeyColumn = (sectionId, keyColumn) => {
    setLayoutConfig(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? { ...section, keyColumn }
          : section
      )
    }));
  };

  // Add new field from database column
  const addFieldFromColumn = (sectionId, columnInfo) => {
    const fieldId = columnInfo.COLUMN_NAME.toLowerCase();
    
    // Use label from config_column if available, otherwise format column name
    let fieldName = columnInfo.label || columnInfo.COLUMN_NAME.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
    
    // Add unit to field name if available
    if (columnInfo.unit && columnInfo.unit.trim()) {
      fieldName += ` (${columnInfo.unit})`;
    }
    
    // Check if field already exists in this section
    const targetSection = layoutConfig.sections.find(s => s.id === sectionId);
    const fieldExists = targetSection?.fields.some(field => 
      field.dbColumn === columnInfo.COLUMN_NAME || field.id === fieldId
    );
    
    if (fieldExists) {
      showToast(`Field "${fieldName}" already exists in this section`);
      return;
    }
    
    // Use format from config_column if available, otherwise determine from data type
    let format = 'text';
    if (columnInfo.format) {
      // Map config_column types to our format types
      const configFormat = columnInfo.format.toLowerCase();
      if (configFormat === 'date') format = 'date';
      else if (configFormat === 'datetime') format = 'time';
      else if (configFormat === 'time') format = 'time';
      else if (configFormat === 'float' || configFormat === 'decimal2' || configFormat === 'decimal4') format = 'number';
      else if (configFormat === 'int' || configFormat === 'comma') format = 'number';
      else if (configFormat === 'currency') format = 'currency';
      else if (configFormat === 'string') format = 'text';
    } else {
      // Fallback to data type analysis
      const dataType = columnInfo.DATA_TYPE?.toLowerCase() || '';
      if (dataType.includes('int') || dataType.includes('float') || dataType.includes('decimal') || dataType.includes('numeric')) {
        format = 'number';
      } else if (dataType.includes('date') || dataType.includes('time')) {
        format = dataType.includes('time') ? 'time' : 'date';
      }
    }

    // Check if this field has lookup configuration
    const currentSection = layoutConfig.sections.find(s => s.id === sectionId);
    const lookupConfig = getLookupConfigFromColumnConfig(currentSection?.tableName, columnInfo.COLUMN_NAME);
    
    // Use the shared utility to generate field configuration
    const newField = generateFieldConfig(columnInfo, {
      useColumnConfigFormat: true,
      defaultGroup: 1,
      defaultWidth: 'auto'
    });
    
    // Add lookup configuration if available
    if (lookupConfig) {
      console.log('🔍 DDR Layout Manager: Adding lookup field configuration for', columnInfo.COLUMN_NAME, lookupConfig);
      newField.format = 'lookup';
      newField.lookupTable = lookupConfig.lookupTable;
      newField.lookupValueColumn = lookupConfig.lookupValueColumn;
      newField.lookupDisplayColumn = lookupConfig.lookupDisplayColumn;
      if (lookupConfig.lookupReturnColumn) {
        newField.lookupReturnColumn = lookupConfig.lookupReturnColumn;
      }
    }

    setLayoutConfig(prev => {
      const updatedSections = prev.sections.map(section =>
        section.id === sectionId
          ? { 
              ...section, 
              fields: [...section.fields, newField]
            }
          : section
      );
      
      // Update selectedSection if it's the one being modified
      if (selectedSection && selectedSection.id === sectionId) {
        const updatedSection = updatedSections.find(s => s.id === sectionId);
        setSelectedSection(updatedSection);
      }
      
      return {
        ...prev,
        sections: updatedSections
      };
    });

    showToast(`Field "${fieldName}" added successfully`);
  };

  // Function to update field group
  const updateFieldGroup = (sectionId, fieldId, newGroup) => {
    setLayoutConfig(prev => {
      const updatedSections = prev.sections.map(section => {
        if (section.id === sectionId) {
          const updatedFields = section.fields.map(field => {
            if (field.id === fieldId) {
              return { ...field, group: parseInt(newGroup) };
            }
            return field;
          });

          // Ensure section has groupSettings
          const groupSettings = section.groupSettings || {
            1: { itemsPerRow: 2, customCSS: '', name: 'Default Group', showGroupName: false }
          };

          // Ensure group settings exist for the new group
                      if (!groupSettings[newGroup]) {
              groupSettings[newGroup] = {
                itemsPerRow: 2,
                customCSS: '',
                name: `Group ${newGroup}`,
                showGroupName: false
              };
            }

          return {
            ...section,
            fields: updatedFields,
            groupSettings
          };
        }
        return section;
      });

      // Update selectedSection if it's the one being modified
      if (selectedSection && selectedSection.id === sectionId) {
        const updatedSection = updatedSections.find(s => s.id === sectionId);
        setSelectedSection(updatedSection);
      }

      return {
        ...prev,
        sections: updatedSections
      };
    });
  };

  // Function to update group settings
  const updateGroupSettings = (sectionId, groupId, settings) => {
    setLayoutConfig(prev => {
      const updatedSections = prev.sections.map(section => {
        if (section.id === sectionId) {
          // Ensure section has groupSettings
          const existingGroupSettings = section.groupSettings || {
            1: { itemsPerRow: 2, customCSS: '', name: 'Default Group', showGroupName: false }
          };

          return {
            ...section,
            groupSettings: {
              ...existingGroupSettings,
              [groupId]: { ...existingGroupSettings[groupId], ...settings }
            }
          };
        }
        return section;
      });

      // Update selectedSection if it's the one being modified
      if (selectedSection && selectedSection.id === sectionId) {
        const updatedSection = updatedSections.find(s => s.id === sectionId);
        setSelectedSection(updatedSection);
      }

      return {
        ...prev,
        sections: updatedSections
      };
    });
  };

  // Function to add new group
  const addNewGroup = (sectionId) => {
    const section = layoutConfig.sections.find(s => s.id === sectionId);
    if (!section) return;

    // Ensure section has groupSettings
    const groupSettings = section.groupSettings || {
      1: { itemsPerRow: 2, customCSS: '', name: 'Default Group', showGroupName: false }
    };

    const existingGroups = Object.keys(groupSettings).map(Number);
    const newGroupId = Math.max(0, ...existingGroups) + 1;

    setLayoutConfig(prev => {
      const updatedSections = prev.sections.map(sec => {
        if (sec.id === sectionId) {
          return {
            ...sec,
            groupSettings: {
              ...sec.groupSettings,
              [newGroupId]: {
                itemsPerRow: 2,
                customCSS: '',
                name: `Group ${newGroupId}`,
                showGroupName: false
              }
            }
          };
        }
        return sec;
      });

      // Update selectedSection if it's the one being modified
      if (selectedSection && selectedSection.id === sectionId) {
        const updatedSection = updatedSections.find(s => s.id === sectionId);
        setSelectedSection(updatedSection);
      }

      return {
        ...prev,
        sections: updatedSections
      };
    });
  };

  // Function to apply CSS template
  const applyCSSTemplate = (sectionId, groupId, templateKey) => {
    const template = cssTemplates[templateKey];
    if (!template) return;

    updateGroupSettings(sectionId, groupId, { customCSS: template.css });
  };

  // Update header HTML
  const updateHeaderHTML = (newHeaderHTML) => {
    setLayoutConfig(prev => ({
      ...prev,
      headerHTML: newHeaderHTML
    }));
  };

  // Update global styles
  const updateGlobalStyles = (styleProperty, value) => {
    setLayoutConfig(prev => ({
      ...prev,
      globalStyles: {
        ...prev.globalStyles,
        [styleProperty]: value
      }
    }));
  };

  // Generate preview HTML with sample data
  const getPreviewHTML = (htmlContent) => {
    if (!htmlContent) return '';
    
    const templateVariables = {
      '{{wellName}}': 'Sample Well Name',
      '{{reportDate}}': '01-Jan-2024', 
      '{{operator}}': 'Sample Operator',
      '{{field}}': 'Sample Field',
      '{{wellType}}': 'Development'
    };

    let previewHTML = htmlContent;
    Object.entries(templateVariables).forEach(([variable, replacement]) => {
      const regex = new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g');
      previewHTML = previewHTML.replace(regex, replacement);
    });

    return previewHTML;
  };

  // Export static HTML for PDF generation
  const exportStaticHTML = () => {
    const staticHTML = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>DDR Header Template</title>
    <style>
        body {
            font-family: ${layoutConfig.globalStyles.fontFamily};
            font-size: ${layoutConfig.globalStyles.fontSize};
            color: ${layoutConfig.globalStyles.textColor};
            background-color: ${layoutConfig.globalStyles.backgroundColor};
            margin: 0;
            padding: 20px;
            width: 180mm; /* A4 content width */
            box-sizing: border-box;
        }
        ${layoutConfig.globalStyles.customCSS || ''}
    </style>
</head>
<body>
    ${layoutConfig.headerHTML}
</body>
</html>`;

    // Create and download the file
    const blob = new Blob([staticHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ddr-header-template.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    showToast('Static HTML template exported successfully!');
  };

  // Apply table CSS template
  const applyTableCSSTemplate = (sectionId, templateKey) => {
    console.log('applyTableCSSTemplate called with:', { sectionId, templateKey });
    const template = tableCssTemplates[templateKey];
    console.log('Found template:', template);
    
    if (!template) {
      console.error('Template not found for key:', templateKey);
      showToast('❌ Template not found');
      return;
    }

    setLayoutConfig(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? { ...section, customTableCSS: template.css }
          : section
      )
    }));

    // Update selectedSection if it's the one being modified
    if (selectedSection && selectedSection.id === sectionId) {
      setSelectedSection(prev => ({
        ...prev,
        customTableCSS: template.css
      }));
    }
    
    showToast('✅ Table template applied successfully');
  };

  // Update section table CSS
  const updateSectionTableCSS = (sectionId, customCSS) => {
    setLayoutConfig(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? { ...section, customTableCSS: customCSS }
          : section
      )
    }));

    // Update selectedSection if it's the one being modified
    if (selectedSection && selectedSection.id === sectionId) {
      setSelectedSection(prev => ({
        ...prev,
        customTableCSS: customCSS
      }));
    }
  };



  // Remove field from section
  const removeField = (sectionId, fieldId) => {
    if (window.confirm('Are you sure you want to remove this field?')) {
      setLayoutConfig(prev => {
        const updatedSections = prev.sections.map(section =>
          section.id === sectionId
            ? { 
                ...section, 
                fields: section.fields.filter(field => field.id !== fieldId)
              }
            : section
        );
        
        // Update selectedSection if it's the one being modified
        if (selectedSection && selectedSection.id === sectionId) {
          const updatedSection = updatedSections.find(s => s.id === sectionId);
          setSelectedSection(updatedSection);
        }
        
        return {
          ...prev,
          sections: updatedSections
        };
      });
      showToast('Field removed successfully');
    }
  };

  // Add new section
  const addNewSection = () => {
    if (!newSectionData.name.trim()) {
      showToast('Section name is required');
      return;
    }

    // Generate unique ID
    const sectionId = newSectionData.name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .replace(/\s+/g, '');

    // Check if section ID already exists
    const existingSection = layoutConfig.sections.find(s => s.id === sectionId);
    if (existingSection) {
      showToast('A section with this name already exists');
      return;
    }

    const newSection = {
      id: sectionId,
      name: newSectionData.name,
      enabled: true,
      presentationType: newSectionData.presentationType,
      order: layoutConfig.sections.length,
      tableName: newSectionData.tableName,
      fields: [],
      groupSettings: {
        1: { itemsPerRow: 2, customCSS: '', name: 'Default Group', showGroupName: false }
      }
    };

    setLayoutConfig(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));

    // Reset form and close modal
    setNewSectionData({
      name: '',
      presentationType: 'label-value',
      tableName: ''
    });
    setShowAddSectionModal(false);
    showToast(`Section "${newSectionData.name}" added successfully`);
  };

  // Remove section
  const removeSection = (sectionId) => {
    if (window.confirm('Are you sure you want to remove this section and all its fields?')) {
      setLayoutConfig(prev => {
        const filteredSections = prev.sections.filter(s => s.id !== sectionId);
        // Reorder the remaining sections
        const reorderedSections = filteredSections.map((section, index) => ({
          ...section,
          order: index
        }));
        
        return {
          ...prev,
          sections: reorderedSections
        };
      });

      // Clear selected section if it was the removed one
      if (selectedSection?.id === sectionId) {
        setSelectedSection(null);
      }
      
      showToast('Section removed successfully');
    }
  };

  // Show toast notification
  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(''), 3000);
  };

  // Load configuration from localStorage
  useEffect(() => {
    const savedConfig = localStorage.getItem('ddrLayoutConfig');
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig);
        const migratedConfig = migrateConfigurationToGroups(parsedConfig);
        setLayoutConfig(migratedConfig);
      } catch (err) {
        console.error('Failed to load saved layout config:', err);
      }
    }
  }, []);

  // Handle drag start
  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop for reordering sections
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (draggedIndex === dropIndex || isNaN(draggedIndex)) return;

    const sortedSections = [...layoutConfig.sections].sort((a, b) => a.order - b.order);
    const draggedSection = sortedSections[draggedIndex];
    
    // Remove dragged item
    sortedSections.splice(draggedIndex, 1);
    
    // Insert at new position
    sortedSections.splice(dropIndex, 0, draggedSection);
    
    // Update order values
    const updatedSections = sortedSections.map((section, index) => ({
      ...section,
      order: index
    }));

    setLayoutConfig({
      ...layoutConfig,
      sections: updatedSections
    });
  };

  // Move section up
  const moveSectionUp = (sectionId) => {
    const sortedSections = [...layoutConfig.sections].sort((a, b) => a.order - b.order);
    const currentIndex = sortedSections.findIndex(s => s.id === sectionId);
    
    if (currentIndex > 0) {
      const newSections = [...sortedSections];
      [newSections[currentIndex], newSections[currentIndex - 1]] = 
      [newSections[currentIndex - 1], newSections[currentIndex]];
      
      const updatedSections = newSections.map((section, index) => ({
        ...section,
        order: index
      }));

      setLayoutConfig({
        ...layoutConfig,
        sections: updatedSections
      });
    }
  };

  // Move section down
  const moveSectionDown = (sectionId) => {
    const sortedSections = [...layoutConfig.sections].sort((a, b) => a.order - b.order);
    const currentIndex = sortedSections.findIndex(s => s.id === sectionId);
    
    if (currentIndex < sortedSections.length - 1) {
      const newSections = [...sortedSections];
      [newSections[currentIndex], newSections[currentIndex + 1]] = 
      [newSections[currentIndex + 1], newSections[currentIndex]];
      
      const updatedSections = newSections.map((section, index) => ({
        ...section,
        order: index
      }));

      setLayoutConfig({
        ...layoutConfig,
        sections: updatedSections
      });
    }
  };

  // Toggle section enabled/disabled
  const toggleSectionEnabled = (sectionId) => {
    setLayoutConfig({
      ...layoutConfig,
      sections: layoutConfig.sections.map(section =>
        section.id === sectionId
          ? { ...section, enabled: !section.enabled }
          : section
      )
    });
  };

  // Update section name
  const updateSectionName = (sectionId, newName) => {
    setLayoutConfig({
      ...layoutConfig,
      sections: layoutConfig.sections.map(section =>
        section.id === sectionId
          ? { ...section, name: newName }
          : section
      )
    });
  };

  // Update section presentation type
  const updatePresentationType = (sectionId, type) => {
    setLayoutConfig(prevConfig => {
      return {
        ...prevConfig,
        sections: prevConfig.sections.map(section => {
          if (section.id === sectionId) {
            // Always preserve both groupSettings and customTableCSS
            return {
              ...section,
              presentationType: type,
              groupSettings: section.groupSettings || { 1: { itemsPerRow: 2, customCSS: '', name: 'Default Group', showGroupName: false } },
              customTableCSS: section.customTableCSS || ''
            };
          }
          return section;
        })
      };
    });
  };

  // Toggle field enabled/disabled
  const toggleFieldEnabled = (sectionId, fieldId) => {
    setLayoutConfig({
      ...layoutConfig,
      sections: layoutConfig.sections.map(section =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map(field =>
                field.id === fieldId
                  ? { ...field, enabled: !field.enabled }
                  : field
              )
            }
          : section
      )
    });
  };

  // Update field property
  const updateFieldProperty = (sectionId, fieldId, property, value) => {
    setLayoutConfig(prevConfig => ({
      ...prevConfig,
      sections: prevConfig.sections.map(section =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map(field =>
                field.id === fieldId
                  ? { ...field, [property]: value }
                  : field
              )
            }
          : section
      )
    }));
    
    // Also update selectedSection if it's the current section
    if (selectedSection && selectedSection.id === sectionId) {
      setSelectedSection(prevSection => ({
        ...prevSection,
        fields: prevSection.fields.map(field =>
          field.id === fieldId
            ? { ...field, [property]: value }
            : field
        )
      }));
    }
  };

  // Move field up
  const moveFieldUp = (sectionId, fieldId) => {
    setLayoutConfig(prev => {
      const updatedSections = prev.sections.map(section => {
        if (section.id === sectionId) {
          const fieldIndex = section.fields.findIndex(f => f.id === fieldId);
          if (fieldIndex > 0) {
            const newFields = [...section.fields];
            [newFields[fieldIndex], newFields[fieldIndex - 1]] = 
            [newFields[fieldIndex - 1], newFields[fieldIndex]];
            return { ...section, fields: newFields };
          }
        }
        return section;
      });
      
      // Update selectedSection if it's the one being modified
      if (selectedSection && selectedSection.id === sectionId) {
        const updatedSection = updatedSections.find(s => s.id === sectionId);
        setSelectedSection(updatedSection);
      }
      
      return {
        ...prev,
        sections: updatedSections
      };
    });
  };

  // Move field down
  const moveFieldDown = (sectionId, fieldId) => {
    setLayoutConfig(prev => {
      const updatedSections = prev.sections.map(section => {
        if (section.id === sectionId) {
          const fieldIndex = section.fields.findIndex(f => f.id === fieldId);
          if (fieldIndex < section.fields.length - 1) {
            const newFields = [...section.fields];
            [newFields[fieldIndex], newFields[fieldIndex + 1]] = 
            [newFields[fieldIndex + 1], newFields[fieldIndex]];
            return { ...section, fields: newFields };
          }
        }
        return section;
      });
      
      // Update selectedSection if it's the one being modified
      if (selectedSection && selectedSection.id === sectionId) {
        const updatedSection = updatedSections.find(s => s.id === sectionId);
        setSelectedSection(updatedSection);
      }
      
      return {
        ...prev,
        sections: updatedSections
      };
    });
  };

  // Handle field drag start
  const handleFieldDragStart = (e, fieldIndex) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', fieldIndex.toString());
  };

  // Handle field drop
  const handleFieldDrop = (e, dropIndex, sectionId) => {
    e.preventDefault();
    e.stopPropagation();
    
    const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (draggedIndex === dropIndex || isNaN(draggedIndex)) return;

    setLayoutConfig(prev => {
      const updatedSections = prev.sections.map(section => {
        if (section.id === sectionId) {
          const newFields = [...section.fields];
          const draggedField = newFields[draggedIndex];
          
          // Remove dragged field
          newFields.splice(draggedIndex, 1);
          
          // Insert at new position
          newFields.splice(dropIndex, 0, draggedField);
          
          return { ...section, fields: newFields };
        }
        return section;
      });
      
      // Update selectedSection if it's the one being modified
      if (selectedSection && selectedSection.id === sectionId) {
        const updatedSection = updatedSections.find(s => s.id === sectionId);
        setSelectedSection(updatedSection);
      }
      
      return {
        ...prev,
        sections: updatedSections
      };
    });
  };

  // Save configuration
  const saveConfiguration = async () => {
    try {
      const response = await apiFetch('/api/ddr-layout/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(layoutConfig),
      });
      
      showToast('✅ Configuration saved successfully to server!');
      if (response.backupFile) {
        console.log('Backup created:', response.backupFile);
      }

      // Dispatch event to notify other components of configuration change
      // This ensures exports immediately reflect the new layout
      const configChangeEvent = new CustomEvent('ddr-layout-config-changed', {
        detail: {
          type: 'ddr-layout-config-saved',
          config: layoutConfig,
          timestamp: new Date().toISOString()
        }
      });
      
      console.log('📡 Dispatching layout configuration change event:', configChangeEvent.detail);
      window.dispatchEvent(configChangeEvent);
      console.log('✅ Event dispatched successfully');
      
    } catch (err) {
      console.error('Failed to save config to backend:', err);
      showToast('❌ Failed to save configuration to server');
    }
  };

  // Reset to default configuration
  const resetToDefault = async () => {
    if (window.confirm('Are you sure you want to reset to default configuration? This will remove all customizations.')) {
      try {
        const response = await apiFetch('/api/ddr-layout/config/reset', {
          method: 'POST',
        });
        
        setLayoutConfig(response.config);
        showToast('✅ Configuration reset to default successfully!');
        if (response.backupFile) {
          console.log('Backup created before reset:', response.backupFile);
        }

        // Dispatch event to notify other components of configuration change
        const configChangeEvent = new CustomEvent('ddr-layout-config-changed', {
          detail: {
            type: 'ddr-layout-config-reset',
            config: response.config,
            timestamp: new Date().toISOString()
          }
        });
        
        console.log('📡 Dispatching layout configuration reset event:', configChangeEvent.detail);
        window.dispatchEvent(configChangeEvent);
        console.log('✅ Reset event dispatched successfully');
        
      } catch (err) {
        console.error('Failed to reset config:', err);
        showToast('❌ Failed to reset configuration');
      }
    }
  };

  // Clear column selection
  const clearColumnSelection = () => {
    setSelectedColumns([]);
  };

  // Enhanced filtering and selection functions
  const filterColumns = (columns) => {
    if (!columns) return [];
    
    let filtered = columns;
    
    // Apply text filter
    if (columnFilter.trim()) {
      const filter = columnFilter.toLowerCase();
      filtered = filtered.filter(column => 
        (column.label || column.COLUMN_NAME).toLowerCase().includes(filter) ||
        (column.COLUMN_NAME).toLowerCase().includes(filter) ||
        (column.desc || '').toLowerCase().includes(filter)
      );
    }
    
    // Apply type filter
    if (selectedColumnTypes.length > 0) {
      filtered = filtered.filter(column => {
        const columnType = (column.format || column.DATA_TYPE || '').toLowerCase();
        return selectedColumnTypes.some(type => columnType.includes(type.toLowerCase()));
      });
    }
    
    return filtered;
  };

  // Get unique column types for filtering
  const getColumnTypes = (columns) => {
    if (!columns) return [];
    
    const types = new Set();
    columns.forEach(column => {
      const type = column.format || column.DATA_TYPE || 'unknown';
      types.add(type);
    });
    
    return Array.from(types).sort();
  };

  // Select columns by type
  const selectColumnsByType = (type) => {
    if (selectedSection?.tableName && tableColumns[selectedSection.tableName]) {
      const columnsOfType = tableColumns[selectedSection.tableName].filter(column => {
        const columnType = (column.format || column.DATA_TYPE || '').toLowerCase();
        const isAlreadyAdded = selectedSection.fields.some(field => 
          field.dbColumn === column.COLUMN_NAME
        );
        return columnType.includes(type.toLowerCase()) && !isAlreadyAdded;
      });
      
      setSelectedColumns(prev => {
        const newSelection = [...prev];
        columnsOfType.forEach(col => {
          if (!newSelection.some(selected => selected.COLUMN_NAME === col.COLUMN_NAME)) {
            newSelection.push(col);
          }
        });
        return newSelection;
      });
    }
  };

  // Enhanced add multiple fields with bulk configuration
  const addMultipleFieldsFromColumnsEnhanced = (sectionId, columnInfoArray, bulkConfig = {}) => {
    const section = layoutConfig.sections.find(s => s.id === sectionId);
    if (!section) return;

    const newFields = [];
    const skippedFields = [];

    columnInfoArray.forEach(columnInfo => {
      const fieldId = columnInfo.COLUMN_NAME.toLowerCase();
      
      // Use label from config_column if available, otherwise format column name
      let fieldName = columnInfo.label || columnInfo.COLUMN_NAME.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
      
      // Add unit to field name if available
      if (columnInfo.unit && columnInfo.unit.trim()) {
        fieldName += ` (${columnInfo.unit})`;
      }
      
      // Check if field already exists in this section
      const fieldExists = section?.fields.some(field => 
        field.dbColumn === columnInfo.COLUMN_NAME || field.id === fieldId
      );
      
      if (fieldExists) {
        skippedFields.push(fieldName);
        return;
      }

      // Use the shared utility to generate field configuration
      const newField = generateFieldConfig(columnInfo, {
        useColumnConfigFormat: true,
        defaultGroup: bulkConfig.group || 1,
        defaultWidth: bulkConfig.width || 'auto'
      });

      // Apply bulk configuration
      if (bulkConfig.group) {
        newField.group = bulkConfig.group;
      }

      newFields.push(newField);
    });

    if (newFields.length > 0) {
      setLayoutConfig(prev => {
        const updatedSections = prev.sections.map(section =>
          section.id === sectionId
            ? { 
                ...section, 
                fields: [...section.fields, ...newFields]
              }
            : section
        );
        
        // Update selectedSection if it's the one being modified
        if (selectedSection && selectedSection.id === sectionId) {
          const updatedSection = updatedSections.find(s => s.id === sectionId);
          setSelectedSection(updatedSection);
        }
        
        return {
          ...prev,
          sections: updatedSections
        };
      });

      const addedCount = newFields.length;
      const skippedCount = skippedFields.length;
      let message = `✅ ${addedCount} field${addedCount > 1 ? 's' : ''} added successfully`;
      if (bulkConfig.group) {
        message += ` to Group ${bulkConfig.group}`;
      }
      if (skippedCount > 0) {
        message += ` (${skippedCount} field${skippedCount > 1 ? 's' : ''} skipped - already exists)`;
      }
      showToast(message);
    } else if (skippedFields.length > 0) {
      showToast(`⚠️ All selected fields already exist in this section`);
    }
  };

  // Handle multiple column selection
  const toggleColumnSelection = (column) => {
    setSelectedColumns(prev => {
      const isSelected = prev.some(col => col.COLUMN_NAME === column.COLUMN_NAME);
      if (isSelected) {
        return prev.filter(col => col.COLUMN_NAME !== column.COLUMN_NAME);
      } else {
        return [...prev, column];
      }
    });
  };

  // Select all columns (with filtering applied)
  const selectAllColumns = () => {
    if (selectedSection?.tableName && tableColumns[selectedSection.tableName]) {
      const filteredColumns = filterColumns(tableColumns[selectedSection.tableName]);
      const availableColumns = filteredColumns.filter(column => {
        return !selectedSection.fields.some(field => 
          field.dbColumn === column.COLUMN_NAME
        );
      });
      setSelectedColumns(availableColumns);
    }
  };

  return (
    <div className="ddr-layout-manager">
      <div className="layout-manager-header">
        <h1>Daily Drilling Report Layout Manager</h1>
        <div className="header-actions">
          <button 
            onClick={() => setShowHeaderEditor(true)} 
            className="header-edit-btn"
            title="Edit Report Header"
          >
            📝 Edit Header
          </button>
          <button 
            onClick={() => setShowGlobalStylesEditor(true)} 
            className="header-edit-btn"
            title="Configure Global Styles"
          >
            🎨 Global Styles
          </button>
          <button onClick={saveConfiguration} className="save-btn">
            💾 Save Configuration
          </button>
          <button onClick={resetToDefault} className="reset-btn">
            🔄 Reset to Default
          </button>
        </div>
      </div>

      <div className="layout-manager-content">
        <div className="sections-panel">
          <div className="sections-panel-header">
            <h2>Sections Configuration</h2>
            <button 
              className="add-section-btn"
              onClick={() => setShowAddSectionModal(true)}
              title="Add new section"
            >
              ➕ Add Section
            </button>
          </div>
          <p className="panel-description">
            Drag and drop to reorder sections. Click on a section to configure its fields.
          </p>

          <div className="sections-list">
            {layoutConfig.sections
              .sort((a, b) => a.order - b.order)
              .map((section, index) => (
                <div
                  key={section.id}
                  className={`section-item ${!section.enabled ? 'disabled' : ''} ${selectedSection?.id === section.id ? 'selected' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  onClick={() => {
                    // Find the current section to ensure we have the latest data
                    const currentSection = layoutConfig.sections.find(s => s.id === section.id);
                    const sectionWithDefaults = {
                      ...(currentSection || section),
                      groupSettings: (currentSection || section).groupSettings || {
                        1: { itemsPerRow: 2, customCSS: '', name: 'Default Group', showGroupName: false }
                      }
                    };
                    setSelectedSection(sectionWithDefaults);
                  }}
                >
                  <div className="drag-handle">
                    ⋮⋮
                  </div>
                  
                  <div className="section-info">
                    <div className="section-header">
                      <input
                        type="text"
                        value={section.name}
                        onChange={(e) => updateSectionName(section.id, e.target.value)}
                        className="section-name-input"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="section-type-badge">
                        {section.presentationType === 'table' ? '📊 Table' : '📋 Label-Value'}
                      </span>
                    </div>
                    <div className="section-meta">
                      <span className="field-count">{section.fields.filter(f => f.enabled).length} / {section.fields.length} fields enabled</span>
                      {section.tableName && <span className="table-badge">📊 {section.tableName}</span>}
                    </div>
                  </div>

                  <div className="section-controls">
                    <div className="move-buttons">
                      <button
                        className="move-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSectionUp(section.id);
                        }}
                        disabled={index === 0}
                        title="Move up"
                      >
                        ⬆️
                      </button>
                      <button
                        className="move-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSectionDown(section.id);
                        }}
                        disabled={index === layoutConfig.sections.length - 1}
                        title="Move down"
                      >
                        ⬇️
                      </button>
                      <button
                        className="remove-section-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSection(section.id);
                        }}
                        title="Remove section"
                      >
                        🗑️
                      </button>
                    </div>
                    <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={section.enabled}
                        onChange={() => toggleSectionEnabled(section.id)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              ))}
          </div>
        </div>

        <div className="fields-panel">
          {selectedSection ? (
            <>
              <div className="fields-panel-header">
                <div className="section-title-area">
                  <h2>Configure: {selectedSection.name}</h2>
                  <div className="section-quick-stats">
                    <span className="stat-badge active-fields">
                      {selectedSection.fields.filter(f => f.enabled).length} Active
                    </span>
                    <span className="stat-badge total-fields">
                      {selectedSection.fields.length} Total
                    </span>
                    {selectedSection.tableName && (
                      <span className="stat-badge db-table">
                        📊 {selectedSection.tableName}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="configuration-content">
                <div className="config-sections">
                  {/* Compact Configuration Section */}
                  <div className="config-section presentation-config">
                    <div className="config-section-header">
                      <h3>📋 Presentation & Data Source</h3>
                    </div>
                    <div className="config-section-content">
                      <div className="config-grid">
                        <div className="config-item">
                          <label>Display Type:</label>
                          <div className="radio-group compact">
                            <label className="radio-option">
                              <input
                                type="radio"
                                value="label-value"
                                checked={selectedSection.presentationType === 'label-value'}
                                onChange={(e) => updatePresentationType(selectedSection.id, e.target.value)}
                              />
                              <span>📋 Form</span>
                            </label>
                            <label className="radio-option">
                              <input
                                type="radio"
                                value="table"
                                checked={selectedSection.presentationType === 'table'}
                                onChange={(e) => updatePresentationType(selectedSection.id, e.target.value)}
                              />
                              <span>📊 Table</span>
                            </label>
                          </div>
                        </div>

                        <div className="config-item">
                          <label>Database Table:</label>
                          <select
                            value={selectedSection.tableName || ''}
                            onChange={(e) => {
                              updateSectionTable(selectedSection.id, e.target.value);
                              if (e.target.value) {
                                fetchTableColumns(e.target.value);
                              }
                            }}
                            className="table-select compact"
                          >
                            <option value="">No table selected</option>
                            {availableTables.map(table => (
                              <option key={table.name || table} value={table.name || table}>
                                {table.label || table.name || table}
                              </option>
                            ))}
                          </select>
                        </div>

                        {selectedSection.tableName && (
                          <div className="config-item">
                            <label>Data Refresh:</label>
                            <select
                              value={selectedSection.keyColumn || ''}
                              onChange={(e) => updateSectionKeyColumn(selectedSection.id, e.target.value)}
                              className="key-column-select compact"
                            >
                              <option value="">Select refresh trigger</option>
                              <option value="wellId">🔧 Well Changes</option>
                              <option value="reportId">📅 Date Changes</option>
                              <option value="wellId+date">🔗 Both Changes</option>
                            </select>
                          </div>
                        )}

                        {selectedSection.tableName && (
                          <div className="config-item add-fields-item">
                            <button
                              className="add-fields-btn"
                              onClick={() => {
                                setSelectedColumns([]);
                                if (!tableColumns[selectedSection.tableName]) {
                                  fetchTableColumns(selectedSection.tableName);
                                }
                                setShowAddFieldModal(true);
                              }}
                              disabled={loadingColumns}
                            >
                              {loadingColumns ? '⏳ Loading...' : '➕ Add Database Fields'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Fields Configuration Section */}
                  <div className="config-section fields-config">
                    <div className="config-section-header">
                      <h3>⚙️ Fields Configuration</h3>
                      <div className="field-actions-toolbar">
                        <span className="field-count-display">
                          {selectedSection.fields.filter(f => f.enabled).length} of {selectedSection.fields.length} enabled
                        </span>
                      </div>
                    </div>
                    <div className="config-section-content scrollable-fields">
                      <div className="fields-list-container">
                        {selectedSection.fields.length > 0 ? (
                          <div className="fields-list">
                            {selectedSection.fields.map((field, fieldIndex) => (
                              <div 
                                key={field.id} 
                                className={`field-item compact ${!field.enabled ? 'disabled' : ''}`}
                                draggable
                                onDragStart={(e) => handleFieldDragStart(e, fieldIndex)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleFieldDrop(e, fieldIndex, selectedSection.id)}
                              >
                                <div className="field-header">
                                  <div className="field-main-controls">
                                    <div className="field-drag-handle" title="Drag to reorder">⋮⋮</div>
                                    
                                    <label className="field-toggle">
                                      <input
                                        type="checkbox"
                                        checked={field.enabled}
                                        onChange={() => toggleFieldEnabled(selectedSection.id, field.id)}
                                      />
                                      <span className="checkmark"></span>
                                    </label>
                                    
                                    <div className="field-name-section">
                                      <input
                                        type="text"
                                        value={field.name}
                                        onChange={(e) => updateFieldProperty(selectedSection.id, field.id, 'name', e.target.value)}
                                        className="field-name-input compact"
                                        disabled={!field.enabled}
                                        placeholder="Field name"
                                      />
                                      <div className="field-meta">
                                        <span className="db-column-tag">{field.dbColumn || 'No DB column'}</span>
                                        <span className="format-tag">{field.format}</span>
                                        <span className="group-tag">Group {field.group || 1}</span>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="field-actions">
                                    <div className="field-move-buttons">
                                      <button
                                        className="field-move-btn"
                                        onClick={() => moveFieldUp(selectedSection.id, field.id)}
                                        disabled={fieldIndex === 0}
                                        title="Move up"
                                      >⬆️</button>
                                      <button
                                        className="field-move-btn"
                                        onClick={() => moveFieldDown(selectedSection.id, field.id)}
                                        disabled={fieldIndex === selectedSection.fields.length - 1}
                                        title="Move down"
                                      >⬇️</button>
                                    </div>
                                    <button
                                      className="remove-field-btn"
                                      onClick={() => removeField(selectedSection.id, field.id)}
                                      title="Remove field"
                                    >🗑️</button>
                                  </div>
                                </div>

                                <div className="field-config-grid">
                                  <div className="config-row">
                                    <label>Database Column:</label>
                                    <input
                                      type="text"
                                      value={field.dbColumn || ''}
                                      onChange={(e) => updateFieldProperty(selectedSection.id, field.id, 'dbColumn', e.target.value)}
                                      disabled={!field.enabled}
                                      className="config-input"
                                      placeholder="Column name"
                                    />
                                  </div>

                                  <div className="config-row">
                                    <label>Group:</label>
                                    <select
                                      value={field.group || 1}
                                      onChange={(e) => updateFieldGroup(selectedSection.id, field.id, e.target.value)}
                                      disabled={!field.enabled}
                                      className="config-select"
                                    >
                                      {Object.keys(selectedSection.groupSettings || {1: { name: 'Default Group', showGroupName: false }}).map(groupId => (
                                        <option key={groupId} value={groupId}>
                                          Group {groupId} - {selectedSection.groupSettings?.[groupId]?.name || `Group ${groupId}`}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div className="config-row">
                                    <label>Format:</label>
                                    <select
                                      value={field.format}
                                      onChange={(e) => {
                                        const newFormat = e.target.value;
                                        updateFieldProperty(selectedSection.id, field.id, 'format', newFormat);
                                        
                                        // Set default formatting when format changes
                                        let defaultFormatting = {};
                                        switch (newFormat) {
                                          case 'text':
                                          case 'textarea':
                                            defaultFormatting = { textFormat: 'normal' };
                                            break;
                                          case 'date':
                                            defaultFormatting = { dateFormat: 'DD/MM/YYYY' };
                                            break;
                                          case 'time':
                                            defaultFormatting = { timeFormat: 'HH:mm' };
                                            break;
                                          case 'number':
                                            defaultFormatting = { numberFormat: '0.00' };
                                            break;
                                          case 'currency':
                                            defaultFormatting = { currencyFormat: '$0.00' };
                                            break;
                                        }
                                        
                                        // Apply default formatting
                                        Object.entries(defaultFormatting).forEach(([key, value]) => {
                                          updateFieldProperty(selectedSection.id, field.id, key, value);
                                        });
                                      }}
                                      disabled={!field.enabled}
                                      className="config-select"
                                    >
                                      {formatOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  {/* Format-specific options */}
                                  {(field.format === 'text' || field.format === 'textarea') && (
                                    <div className="config-row">
                                      <label>Text Style:</label>
                                      <select
                                        value={field.textFormat || 'normal'}
                                        onChange={(e) => updateFieldProperty(selectedSection.id, field.id, 'textFormat', e.target.value)}
                                        disabled={!field.enabled}
                                        className="config-select"
                                      >
                                        {TEXT_FORMAT_OPTIONS.map(option => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}

                                  {field.format === 'date' && (
                                    <div className="config-row">
                                      <label>Date Format:</label>
                                      <select
                                        value={field.dateFormat || 'DD/MM/YYYY'}
                                        onChange={(e) => updateFieldProperty(selectedSection.id, field.id, 'dateFormat', e.target.value)}
                                        disabled={!field.enabled}
                                        className="config-select"
                                      >
                                        {DATE_FORMAT_OPTIONS.map(option => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}

                                  {field.format === 'time' && (
                                    <div className="config-row">
                                      <label>Time Format:</label>
                                      <select
                                        value={field.timeFormat || 'HH:mm'}
                                        onChange={(e) => updateFieldProperty(selectedSection.id, field.id, 'timeFormat', e.target.value)}
                                        disabled={!field.enabled}
                                        className="config-select"
                                      >
                                        {TIME_FORMAT_OPTIONS.map(option => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}

                                  {field.format === 'number' && (
                                    <div className="config-row">
                                      <label>Number Format:</label>
                                      <select
                                        value={field.numberFormat || '0.00'}
                                        onChange={(e) => updateFieldProperty(selectedSection.id, field.id, 'numberFormat', e.target.value)}
                                        disabled={!field.enabled}
                                        className="config-select"
                                      >
                                        {NUMBER_FORMAT_OPTIONS.map(option => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}

                                  {field.format === 'currency' && (
                                    <div className="config-row">
                                      <label>Currency Format:</label>
                                      <select
                                        value={field.currencyFormat || '$0.00'}
                                        onChange={(e) => updateFieldProperty(selectedSection.id, field.id, 'currencyFormat', e.target.value)}
                                        disabled={!field.enabled}
                                        className="config-select"
                                      >
                                        {CURRENCY_FORMAT_OPTIONS.map(option => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}

                                  {field.format === 'coordinate' && (
                                    <div className="config-row">
                                      <label>Coordinate Format:</label>
                                      <select
                                        value={field.coordinateFormat || 'dms'}
                                        onChange={(e) => updateFieldProperty(selectedSection.id, field.id, 'coordinateFormat', e.target.value)}
                                        disabled={!field.enabled}
                                        className="config-select"
                                      >
                                        {COORDINATE_FORMAT_OPTIONS.map(option => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}

                                  {field.format === 'lookup' && (
                                    <>
                                      <div className="config-row">
                                        <label>Lookup Table:</label>
                                        <input
                                          type="text"
                                          value={field.lookupTable || ''}
                                          onChange={(e) => updateFieldProperty(selectedSection.id, field.id, 'lookupTable', e.target.value)}
                                          disabled={!field.enabled}
                                          className="config-input"
                                          placeholder="e.g., JobCode"
                                        />
                                      </div>
                                      <div className="config-row">
                                        <label>Value Column:</label>
                                        <input
                                          type="text"
                                          value={field.lookupValueColumn || ''}
                                          onChange={(e) => updateFieldProperty(selectedSection.id, field.id, 'lookupValueColumn', e.target.value)}
                                          disabled={!field.enabled}
                                          className="config-input"
                                          placeholder="e.g., Code"
                                        />
                                      </div>
                                      <div className="config-row">
                                        <label>Display Column:</label>
                                        <input
                                          type="text"
                                          value={field.lookupDisplayColumn || ''}
                                          onChange={(e) => updateFieldProperty(selectedSection.id, field.id, 'lookupDisplayColumn', e.target.value)}
                                          disabled={!field.enabled}
                                          className="config-input"
                                          placeholder="e.g., Description or Code,Description"
                                        />
                                      </div>
                                      <div className="config-row">
                                        <label>Return Columns (Multi-Field):</label>
                                        <input
                                          type="text"
                                          value={field.lookupReturnColumn || ''}
                                          onChange={(e) => updateFieldProperty(selectedSection.id, field.id, 'lookupReturnColumn', e.target.value)}
                                          disabled={!field.enabled}
                                          className="config-input"
                                          placeholder="e.g., Name,Position,Company"
                                        />
                                      </div>
                                      <div className="config-row">
                                        <div className="lookup-info">
                                          <small>
                                            💡 <strong>Multi-Column Features:</strong><br/>
                                            • <strong>Display Column:</strong> Use comma-separated columns for multi-column dropdown display (e.g., "Code,Description")<br/>
                                            • <strong>Return Columns:</strong> Specify columns to populate other form fields when selection is made (e.g., "Name,Position,Company")<br/>
                                            • Configuration is automatically detected from Column Configuration and can be overridden here.
                                          </small>
                                        </div>
                                      </div>
                                    </>
                                  )}

                                  {selectedSection.presentationType === 'table' && (
                                    <div className="config-row">
                                      <label>Width:</label>
                                      <select
                                        value={field.width}
                                        onChange={(e) => updateFieldProperty(selectedSection.id, field.id, 'width', e.target.value)}
                                        disabled={!field.enabled}
                                        className="config-select"
                                      >
                                        {FIELD_WIDTH_OPTIONS.map(option => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="empty-fields-state">
                            <div className="empty-icon">📝</div>
                            <h4>No Fields Added</h4>
                            <p>Add fields from the database table above to configure this section.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Group Settings Section - Only for label-value presentation */}
                  {selectedSection.presentationType === 'label-value' && (
                    <div className="config-section group-config">
                      <div className="config-section-header">
                        <h3>👥 Group Layout Settings</h3>
                        <button
                          className="add-group-btn"
                          onClick={() => addNewGroup(selectedSection.id)}
                          title="Add new group"
                        >
                          ➕ Add Group
                        </button>
                      </div>
                      <div className="config-section-content">
                        <div className="groups-grid">
                          {Object.entries(selectedSection.groupSettings || {1: { itemsPerRow: 2, customCSS: '', name: 'Default Group', showGroupName: false }}).map(([groupId, groupSettings]) => (
                            <div key={groupId} className="group-settings-card">
                              <div className="group-card-header">
                                <h4>Group {groupId}</h4>
                                <span className="group-field-count">
                                  {selectedSection.fields.filter(f => f.group == groupId && f.enabled).length} fields
                                </span>
                              </div>
                              
                              <div className="group-config-grid">
                                <div className="config-item">
                                  <label>Group Name:</label>
                                  <input
                                    type="text"
                                    value={groupSettings.name || `Group ${groupId}`}
                                    onChange={(e) => updateGroupSettings(selectedSection.id, groupId, { name: e.target.value })}
                                    className="config-input"
                                    placeholder={`Group ${groupId} name`}
                                  />
                                </div>
                                
                                <div className="config-item">
                                  <label>Items per Row:</label>
                                  <select
                                    value={groupSettings.itemsPerRow || 2}
                                    onChange={(e) => updateGroupSettings(selectedSection.id, groupId, { itemsPerRow: parseInt(e.target.value) })}
                                    className="config-select"
                                  >
                                    {[1,2,3,4,5,6].map(num => (
                                      <option key={num} value={num}>{num} per row</option>
                                    ))}
                                  </select>
                                </div>

                                <div className="config-item">
                                  <label className="toggle-label">
                                    <input
                                      type="checkbox"
                                      checked={groupSettings.showGroupName || false}
                                      onChange={(e) => updateGroupSettings(selectedSection.id, groupId, { showGroupName: e.target.checked })}
                                      className="group-name-toggle"
                                    />
                                    <span className="toggle-text">Show Group Name</span>
                                  </label>
                                </div>
                                
                                <div className="config-item">
                                  <label>CSS Template:</label>
                                  <select
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        applyCSSTemplate(selectedSection.id, groupId, e.target.value);
                                      }
                                    }}
                                    className="config-select"
                                    value=""
                                  >
                                    <option value="">Select template...</option>
                                    {Object.entries(cssTemplates).map(([key, template]) => (
                                      <option key={key} value={key}>
                                        {template.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div className="config-item full-width">
                                  <label>Custom Group CSS:</label>
                                  <CSSEditor
                                    value={groupSettings.customCSS || ''}
                                    onChange={(value) => updateGroupSettings(selectedSection.id, groupId, { customCSS: value })}
                                    placeholder={`/* Custom CSS for Group ${groupId} */\n.group-${groupId} {\n  /* Add your styles here */\n}`}
                                  />
                                  <div className="css-help-text">
                                    <small>
                                      💡 <strong>CSS Tips:</strong> Target this group with <code>.group-{groupId}</code> selector. 
                                      Common properties: <code>background</code>, <code>border</code>, <code>padding</code>, <code>margin</code>, <code>border-radius</code>
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Table CSS Settings Section - Only for table presentation */}
                  {selectedSection.presentationType === 'table' && (
                    <div className="config-section table-style-config">
                      <div className="config-section-header">
                        <h3>🎨 Table Styling</h3>
                      </div>
                      <div className="config-section-content">
                        <div className="table-style-grid">
                          <div className="config-item">
                            <label>CSS Template:</label>
                            <select
                              onChange={(e) => {
                                if (e.target.value) {
                                  applyTableCSSTemplate(selectedSection.id, e.target.value);
                                }
                              }}
                              className="config-select"
                              value=""
                            >
                              <option value="">Select table template...</option>
                              {Object.entries(tableCssTemplates).map(([key, template]) => (
                                <option key={key} value={key}>
                                  {template.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="config-item full-width">
                            <label>Custom Table CSS:</label>
                            <textarea
                              value={selectedSection.customTableCSS || ''}
                              onChange={(e) => updateSectionTableCSS(selectedSection.id, e.target.value)}
                              className="config-textarea"
                              placeholder="Enter custom CSS for table elements..."
                              rows={4}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <div className="no-selection-content">
                <div className="empty-icon">📋</div>
                <h2>Select a Section</h2>
                <p>Click on a section from the left panel to configure its fields and presentation format.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Add Field Modal with Multiple Selection */}
      {showAddFieldModal && (
        <div className="modal-overlay">
          <div className="add-field-modal enhanced-modal">
            <div className="modal-header">
              <div className="modal-title-section">
                <h3>Add Fields from {selectedSection?.tableName}</h3>
                <p className="modal-subtitle">Select one or more columns to add as fields</p>
              </div>
              <button 
                className="close-modal-btn"
                onClick={() => {
                  setShowAddFieldModal(false);
                  setSelectedColumns([]);
                }}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              {loadingColumns ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading table columns...</p>
                </div>
              ) : (
                <>
                  {/* Enhanced Filter Controls */}
                  <div className="filter-controls">
                    <div className="filter-row">
                      <div className="filter-group">
                        <label>🔍 Search Columns:</label>
                        <input
                          type="text"
                          value={columnFilter}
                          onChange={(e) => setColumnFilter(e.target.value)}
                          placeholder="Search by name, type, or description..."
                          className="filter-input"
                        />
                      </div>
                    </div>
                    
                    <div className="filter-row">
                      <div className="filter-group">
                        <label>📊 Filter by Type:</label>
                        <div className="type-filters">
                          {getColumnTypes(tableColumns[selectedSection?.tableName] || []).map(type => (
                            <button
                              key={type}
                              className={`type-filter-btn ${selectedColumnTypes.includes(type) ? 'active' : ''}`}
                              onClick={() => {
                                setSelectedColumnTypes(prev => 
                                  prev.includes(type) 
                                    ? prev.filter(t => t !== type)
                                    : [...prev, type]
                                );
                              }}
                            >
                              {type}
                            </button>
                          ))}
                          <button
                            className="clear-type-filter-btn"
                            onClick={() => setSelectedColumnTypes([])}
                            disabled={selectedColumnTypes.length === 0}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="selection-controls">
                    <div className="selection-info">
                      <span className="selected-count">
                        {selectedColumns.length} column{selectedColumns.length !== 1 ? 's' : ''} selected
                      </span>
                      {(columnFilter || selectedColumnTypes.length > 0) && (
                        <span className="filter-count">
                          ({filterColumns(tableColumns[selectedSection?.tableName] || []).length} shown)
                        </span>
                      )}
                    </div>
                    <div className="selection-actions">
                      <button
                        className="select-all-btn secondary-btn"
                        onClick={selectAllColumns}
                        disabled={!tableColumns[selectedSection?.tableName]?.length}
                      >
                        Select All {columnFilter || selectedColumnTypes.length > 0 ? 'Filtered' : 'Available'}
                      </button>
                      <button
                        className="clear-selection-btn secondary-btn"
                        onClick={clearColumnSelection}
                        disabled={selectedColumns.length === 0}
                      >
                        Clear Selection
                      </button>
                    </div>
                  </div>

                  <div className="columns-list enhanced-columns-list">
                    {(() => {
                      const filteredColumns = filterColumns(tableColumns[selectedSection?.tableName] || []);
                      return filteredColumns.length > 0 ? (
                        filteredColumns.map((column) => {
                        const isAlreadyAdded = selectedSection.fields.some(field => 
                          field.dbColumn === column.COLUMN_NAME
                        );
                        const isSelected = selectedColumns.some(col => col.COLUMN_NAME === column.COLUMN_NAME);
                        
                        return (
                          <div 
                            key={column.COLUMN_NAME} 
                            className={`column-item enhanced-column-item ${isAlreadyAdded ? 'already-added' : ''} ${isSelected ? 'selected' : ''}`}
                          >
                            <div className="column-selection">
                              <label className="column-checkbox">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleColumnSelection(column)}
                                  disabled={isAlreadyAdded}
                                />
                                <span className="checkmark"></span>
                              </label>
                            </div>
                            
                            <div className="column-info">
                              <div className="column-main-info">
                                <span className="column-name">
                                  {column.label || column.COLUMN_NAME}
                                </span>
                                {column.unit && column.unit.trim() && (
                                  <span className="column-unit">({column.unit})</span>
                                )}
                              </div>
                              <div className="column-meta">
                                <span className="column-type">{column.format || column.DATA_TYPE}</span>
                                <span className="column-db-name">DB: {column.COLUMN_NAME}</span>
                              </div>
                              {column.desc && (
                                <div className="column-description">{column.desc}</div>
                              )}
                            </div>

                            <div className="column-status">
                              {isAlreadyAdded && <span className="status-badge added">✓ Added</span>}
                              {isSelected && !isAlreadyAdded && <span className="status-badge selected">✓ Selected</span>}
                            </div>
                          </div>
                        );
                        })
                      ) : (
                        <div className="empty-state">
                          <div className="empty-icon">📋</div>
                          <h4>No Columns Found</h4>
                          <p>
                            {(columnFilter || selectedColumnTypes.length > 0) 
                              ? 'No columns match your filter criteria.' 
                              : 'No columns were found for this table. Please check the table configuration.'
                            }
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <div className="footer-info">
                {selectedColumns.length > 0 && (
                  <>
                    <span className="selection-summary">
                      Ready to add {selectedColumns.length} field{selectedColumns.length !== 1 ? 's' : ''}
                    </span>
                    <div className="bulk-config">
                      <label>Assign to Group:</label>
                      <select
                        value={bulkGroupAssignment}
                        onChange={(e) => setBulkGroupAssignment(parseInt(e.target.value))}
                        className="bulk-group-select"
                      >
                        {Object.keys(selectedSection?.groupSettings || {1: {}}).map(groupId => (
                          <option key={groupId} value={groupId}>
                            Group {groupId} - {selectedSection?.groupSettings?.[groupId]?.name || `Group ${groupId}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
              <div className="footer-actions">
                <button
                  className="cancel-btn secondary-btn"
                  onClick={() => {
                    setShowAddFieldModal(false);
                    setSelectedColumns([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="add-selected-btn primary-btn"
                  onClick={() => {
                    if (selectedColumns.length > 0) {
                      addMultipleFieldsFromColumnsEnhanced(selectedSection.id, selectedColumns, {
                        group: bulkGroupAssignment
                      });
                      setShowAddFieldModal(false);
                      setSelectedColumns([]);
                      setColumnFilter('');
                      setSelectedColumnTypes([]);
                      setBulkGroupAssignment(1);
                    }
                  }}
                  disabled={selectedColumns.length === 0}
                >
                  Add {selectedColumns.length > 0 ? `${selectedColumns.length} ` : ''}Field{selectedColumns.length !== 1 ? 's' : ''}
                  {selectedColumns.length > 0 && bulkGroupAssignment && ` to Group ${bulkGroupAssignment}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Section Modal */}
      {showAddSectionModal && (
        <div className="modal-overlay">
          <div className="add-section-modal">
            <div className="modal-header">
              <h3>Add New Section</h3>
              <button 
                className="close-modal-btn"
                onClick={() => {
                  setShowAddSectionModal(false);
                  setNewSectionData({
                    name: '',
                    presentationType: 'label-value',
                    tableName: ''
                  });
                }}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <div className="form-group">
                <label>Section Name *</label>
                <input
                  type="text"
                  value={newSectionData.name}
                  onChange={(e) => setNewSectionData(prev => ({
                    ...prev,
                    name: e.target.value
                  }))}
                  placeholder="Enter section name..."
                  className="section-name-input"
                />
              </div>

              <div className="form-group">
                <label>Presentation Type</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      value="label-value"
                      checked={newSectionData.presentationType === 'label-value'}
                      onChange={(e) => setNewSectionData(prev => ({
                        ...prev,
                        presentationType: e.target.value
                      }))}
                    />
                    <span>📋 Label-Value List</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      value="table"
                      checked={newSectionData.presentationType === 'table'}
                      onChange={(e) => setNewSectionData(prev => ({
                        ...prev,
                        presentationType: e.target.value
                      }))}
                    />
                    <span>📊 Table</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Database Table (Optional)</label>
                <select
                  value={newSectionData.tableName}
                  onChange={(e) => setNewSectionData(prev => ({
                    ...prev,
                    tableName: e.target.value
                  }))}
                  className="table-select"
                >
                  <option value="">No table selected</option>
                  {availableTables.map(table => (
                    <option key={table.name || table} value={table.name || table}>
                      {table.label || table.name || table}
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => {
                    setShowAddSectionModal(false);
                    setNewSectionData({
                      name: '',
                      presentationType: 'label-value',
                      tableName: ''
                    });
                  }}
                >
                  Cancel
                </button>
                <button
                  className="add-btn"
                  onClick={addNewSection}
                  disabled={!newSectionData.name.trim()}
                >
                  Add Section
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header Editor Modal */}
      {showHeaderEditor && (
        <div className="modal-overlay">
          <div className="header-editor-modal">
            <div className="modal-header">
              <h3>Edit Report Header</h3>
              <button 
                className="close-modal-btn"
                onClick={() => setShowHeaderEditor(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <div className="header-help">
                <h4>Available Template Variables:</h4>
                <div className="template-variables">
                  <span className="template-var">{'{{wellName}}'}</span>
                  <span className="template-var">{'{{reportDate}}'}</span>
                  <span className="template-var">{'{{operator}}'}</span>
                  <span className="template-var">{'{{field}}'}</span>
                  <span className="template-var">{'{{wellType}}'}</span>
                </div>
                <p className="help-text">
                  Use these variables in your HTML to display dynamic content. 
                  The WYSIWYG editor below allows you to design the header with rich formatting.
                </p>
              </div>

              <div className="form-group">
                <label>Header HTML Content</label>
                <HTMLEditor
                  value={layoutConfig.headerHTML || ''}
                  onChange={updateHeaderHTML}
                  placeholder="Enter your header HTML content. Use the toolbar buttons to format text and insert template variables."
                />
              </div>

              <div className="header-preview">
                <div className="preview-header">
                  <h4>Preview:</h4>
                  <button
                    className="export-html-btn"
                    onClick={exportStaticHTML}
                    title="Export static HTML template for PDF generation"
                  >
                    📄 Export HTML
                  </button>
                </div>
                <div 
                  className="header-preview-content"
                  dangerouslySetInnerHTML={{ 
                    __html: getPreviewHTML(layoutConfig.headerHTML || '')
                  }}
                />
              </div>

              <div className="modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowHeaderEditor(false)}
                >
                  Cancel
                </button>
                <button
                  className="save-btn"
                  onClick={() => {
                    setShowHeaderEditor(false);
                    showToast('✅ Header updated successfully!');
                  }}
                >
                  Save Header
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Global Styles Editor Modal */}
      {showGlobalStylesEditor && (
        <div className="modal-overlay">
          <div className="header-editor-modal">
            <div className="modal-header">
              <h3>Configure Global Styles</h3>
              <button 
                className="close-modal-btn"
                onClick={() => setShowGlobalStylesEditor(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <div className="header-help">
                <h4>Global Report Styling:</h4>
                <p className="help-text">
                  Configure the overall appearance of your Daily Drilling Report. 
                  These settings will apply to all sections unless overridden by specific section styles.
                </p>
              </div>

              <div className="form-group">
                <label>Font Family</label>
                <select
                  value={layoutConfig.globalStyles?.fontFamily || 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'}
                  onChange={(e) => updateGlobalStyles('fontFamily', e.target.value)}
                  className="table-select"
                >
                  <option value="Segoe UI, Tahoma, Geneva, Verdana, sans-serif">Segoe UI (Default)</option>
                  <option value="Arial, sans-serif">Arial</option>
                  <option value="Helvetica, Arial, sans-serif">Helvetica</option>
                  <option value="Times New Roman, serif">Times New Roman</option>
                  <option value="Georgia, serif">Georgia</option>
                  <option value="Courier New, monospace">Courier New</option>
                  <option value="Roboto, sans-serif">Roboto</option>
                  <option value="Open Sans, sans-serif">Open Sans</option>
                </select>
              </div>

              <div className="config-row">
                <div className="form-group">
                  <label>Font Size</label>
                  <select
                    value={layoutConfig.globalStyles?.fontSize || '14px'}
                    onChange={(e) => updateGlobalStyles('fontSize', e.target.value)}
                    className="format-select"
                  >
                    <option value="12px">12px (Small)</option>
                    <option value="13px">13px</option>
                    <option value="14px">14px (Default)</option>
                    <option value="15px">15px</option>
                    <option value="16px">16px (Large)</option>
                    <option value="18px">18px (Extra Large)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Text Color</label>
                  <input
                    type="color"
                    value={layoutConfig.globalStyles?.textColor || '#1e293b'}
                    onChange={(e) => updateGlobalStyles('textColor', e.target.value)}
                    className="color-input"
                  />
                </div>
              </div>

              <div className="config-row">
                <div className="form-group">
                  <label>Background Color</label>
                  <input
                    type="color"
                    value={layoutConfig.globalStyles?.backgroundColor || '#ffffff'}
                    onChange={(e) => updateGlobalStyles('backgroundColor', e.target.value)}
                    className="color-input"
                  />
                </div>

                <div className="form-group">
                  <label>Border Color</label>
                  <input
                    type="color"
                    value={layoutConfig.globalStyles?.borderColor || '#e5e7eb'}
                    onChange={(e) => updateGlobalStyles('borderColor', e.target.value)}
                    className="color-input"
                  />
                </div>
              </div>

              <div className="config-row">
                <div className="form-group">
                  <label>Section Spacing</label>
                  <select
                    value={layoutConfig.globalStyles?.sectionSpacing || '20px'}
                    onChange={(e) => updateGlobalStyles('sectionSpacing', e.target.value)}
                    className="format-select"
                  >
                    <option value="0px">0px (No Spacing)</option>
                    <option value="4px">4px (Minimal)</option>
                    <option value="8px">8px (Tight)</option>
                    <option value="12px">12px (Compact)</option>
                    <option value="16px">16px (Normal)</option>
                    <option value="20px">20px (Default)</option>
                    <option value="24px">24px (Spacious)</option>
                    <option value="32px">32px (Wide)</option>
                    <option value="40px">40px (Extra Wide)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Section Padding</label>
                  <select
                    value={layoutConfig.globalStyles?.sectionPadding || '16px'}
                    onChange={(e) => updateGlobalStyles('sectionPadding', e.target.value)}
                    className="format-select"
                  >
                    <option value="0px">0px (No Padding)</option>
                    <option value="2px">2px (Minimal)</option>
                    <option value="4px">4px (Very Tight)</option>
                    <option value="8px">8px (Tight)</option>
                    <option value="12px">12px (Compact)</option>
                    <option value="16px">16px (Default)</option>
                    <option value="20px">20px (Comfortable)</option>
                    <option value="24px">24px (Spacious)</option>
                    <option value="32px">32px (Extra Spacious)</option>
                  </select>
                </div>
              </div>

              <div className="config-row">
                <div className="form-group">
                  <label>Group Spacing</label>
                  <select
                    value={layoutConfig.globalStyles?.groupSpacing || '16px'}
                    onChange={(e) => updateGlobalStyles('groupSpacing', e.target.value)}
                    className="format-select"
                  >
                    <option value="0px">0px (No Spacing)</option>
                    <option value="4px">4px (Minimal)</option>
                    <option value="8px">8px (Tight)</option>
                    <option value="12px">12px (Compact)</option>
                    <option value="16px">16px (Default)</option>
                    <option value="20px">20px (Comfortable)</option>
                    <option value="24px">24px (Spacious)</option>
                    <option value="32px">32px (Wide)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Group Padding</label>
                  <select
                    value={layoutConfig.globalStyles?.groupPadding || '12px'}
                    onChange={(e) => updateGlobalStyles('groupPadding', e.target.value)}
                    className="format-select"
                  >
                    <option value="0px">0px (No Padding)</option>
                    <option value="4px">4px (Minimal)</option>
                    <option value="8px">8px (Tight)</option>
                    <option value="12px">12px (Default)</option>
                    <option value="16px">16px (Comfortable)</option>
                    <option value="20px">20px (Spacious)</option>
                    <option value="24px">24px (Extra Spacious)</option>
                  </select>
                </div>
              </div>

              <div className="config-row">
                <div className="form-group">
                  <label>Report Container Padding</label>
                  <select
                    value={layoutConfig.globalStyles?.reportContainerPadding || '20px'}
                    onChange={(e) => updateGlobalStyles('reportContainerPadding', e.target.value)}
                    className="format-select"
                  >
                    <option value="0px">0px (No Padding)</option>
                    <option value="4px">4px (Minimal)</option>
                    <option value="8px">8px (Tight)</option>
                    <option value="12px">12px (Compact)</option>
                    <option value="16px">16px (Normal)</option>
                    <option value="20px">20px (Default)</option>
                    <option value="24px">24px (Comfortable)</option>
                    <option value="32px">32px (Spacious)</option>
                    <option value="40px">40px (Extra Spacious)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Report Container Spacing</label>
                  <select
                    value={layoutConfig.globalStyles?.reportContainerSpacing || '0px'}
                    onChange={(e) => updateGlobalStyles('reportContainerSpacing', e.target.value)}
                    className="format-select"
                  >
                    <option value="0px">0px (No Spacing)</option>
                    <option value="4px">4px (Minimal)</option>
                    <option value="8px">8px (Tight)</option>
                    <option value="12px">12px (Compact)</option>
                    <option value="16px">16px (Normal)</option>
                    <option value="20px">20px (Comfortable)</option>
                    <option value="24px">24px (Spacious)</option>
                    <option value="32px">32px (Wide)</option>
                    <option value="40px">40px (Extra Wide)</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Border Radius</label>
                <select
                  value={layoutConfig.globalStyles?.borderRadius || '8px'}
                  onChange={(e) => updateGlobalStyles('borderRadius', e.target.value)}
                  className="format-select"
                >
                  <option value="0px">0px (Square)</option>
                  <option value="4px">4px (Slight)</option>
                  <option value="8px">8px (Default)</option>
                  <option value="12px">12px (Rounded)</option>
                  <option value="16px">16px (Very Rounded)</option>
                </select>
              </div>

              {/* Section Header Styling */}
              <div className="config-section-header">
                <h4>Section Header Styling</h4>
              </div>

              <div className="config-row">
                <div className="form-group">
                  <label>Section Header Font Size</label>
                  <select
                    value={layoutConfig.globalStyles?.sectionHeaderFontSize || '16px'}
                    onChange={(e) => updateGlobalStyles('sectionHeaderFontSize', e.target.value)}
                    className="format-select"
                  >
                    <option value="10px">10px (Very Small)</option>
                    <option value="12px">12px (Small)</option>
                    <option value="14px">14px (Normal)</option>
                    <option value="16px">16px (Default)</option>
                    <option value="18px">18px (Large)</option>
                    <option value="20px">20px (Extra Large)</option>
                    <option value="24px">24px (Huge)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Section Header Background Color</label>
                  <input
                    type="color"
                    value={layoutConfig.globalStyles?.sectionHeaderBackgroundColor || '#f8fafc'}
                    onChange={(e) => updateGlobalStyles('sectionHeaderBackgroundColor', e.target.value)}
                    className="color-input"
                  />
                </div>
              </div>

              <div className="config-row">
                <div className="form-group">
                  <label>Section Header Text Color</label>
                  <input
                    type="color"
                    value={layoutConfig.globalStyles?.sectionHeaderTextColor || '#1e293b'}
                    onChange={(e) => updateGlobalStyles('sectionHeaderTextColor', e.target.value)}
                    className="color-input"
                  />
                </div>

                <div className="form-group">
                  <label>Section Header Font Weight</label>
                  <select
                    value={layoutConfig.globalStyles?.sectionHeaderFontWeight || '600'}
                    onChange={(e) => updateGlobalStyles('sectionHeaderFontWeight', e.target.value)}
                    className="format-select"
                  >
                    <option value="400">400 (Normal)</option>
                    <option value="500">500 (Medium)</option>
                    <option value="600">600 (Semi-Bold)</option>
                    <option value="700">700 (Bold)</option>
                    <option value="800">800 (Extra Bold)</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Custom CSS (Advanced)</label>
                <textarea
                  value={layoutConfig.globalStyles?.customCSS || ''}
                  onChange={(e) => updateGlobalStyles('customCSS', e.target.value)}
                  placeholder="Add custom CSS rules here..."
                  className="custom-css-textarea"
                  rows="6"
                />
                <small className="help-text">
                  Add custom CSS rules to override default styling. Use standard CSS syntax.
                </small>
              </div>

              <div className="modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowGlobalStylesEditor(false)}
                >
                  Cancel
                </button>
                <button
                  className="save-btn"
                  onClick={() => {
                    setShowGlobalStylesEditor(false);
                    showToast('✅ Global styles updated successfully!');
                  }}
                >
                  Save Styles
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="toast-notification">
          {toast}
        </div>
      )}
    </div>
  );
};

export default DDRLayoutManager; 